// The AE-half reviewer profile, rendered compactly outside the Details page:
//  - under a preprint in reviewer-finding, read-only for students (July-2 FR-J11), and
//  - behind the per-preprint "Reviewer profile" button in Quality Check, read + RBAC-gated
//    edit so checkers know the target and can adjust it (FR-J16).
// Headings match the pitch-deck Reviewer Profile prompts (Angel wording).
import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  AE_SUBDISCIPLINES_KEY,
  KEY_REVIEWER_QUESTION_KEY,
  aeSubdisciplines,
  type SelectedView,
} from "@rrid/shared-types";
import { api } from "../../lib/api.js";
import { qk } from "../../lib/queryKeys.js";
import { Badge, Button, Field, Spinner, Textarea, Input } from "../../components/ui.js";
import { RichText } from "../../components/RichText.js";

const PROFILE_FIELDS = [
  {
    key: "Reviewer Profile(discipline)",
    label: "What training / education should a reviewer have? (e.g. epidemiology)",
  },
  {
    key: "Reviewer Profile(method expertise)",
    label: "Please describe the minimum reviewer profile (describe the type of research they conduct)",
  },
  {
    key: "Reviewer Profile(topical expertise)",
    label: "Please list additional desired (but not required) reviewer characteristics (methods and/or research topics)",
  },
] as const;

const KEY_Q_LABEL =
  "What is the most important specific question that the reviewer needs to answer about this preprint?";

export function ReviewerProfilePanel({ selectedId, canEdit = false }: { selectedId: string; canEdit?: boolean }): JSX.Element {
  const qc = useQueryClient();
  const { data: s, isLoading } = useQuery({
    queryKey: qk.selectedOne(selectedId),
    queryFn: () => api.get<SelectedView>(`/preprints/selected/${selectedId}`),
  });
  const [draft, setDraft] = useState<Record<string, string>>({});
  const dirty = Object.keys(draft).length > 0;

  const save = useMutation({
    mutationFn: () => api.patch<SelectedView>(`/preprints/selected/${selectedId}`, { pitch: draft }),
    onSuccess: (updated) => {
      qc.setQueryData(qk.selectedOne(selectedId), updated);
      setDraft({});
    },
  });

  if (isLoading) return <Spinner label="Loading reviewer profile…" />;
  if (!s) return <p className="text-xs text-slate-500">Reviewer profile unavailable.</p>;

  const pitch = s.pitch as Record<string, unknown>;
  const valueOf = (key: string): string => (key in draft ? draft[key]! : String(pitch[key] ?? ""));
  const aeSubs = aeSubdisciplines({ ...pitch, ...draft });
  const keyQuestion = valueOf(KEY_REVIEWER_QUESTION_KEY).trim();
  const similarArticles = valueOf("Nearest Neighbor Papers").trim();

  return (
    <div className="space-y-3">
      {PROFILE_FIELDS.map(({ key, label }) =>
        canEdit ? (
          <Field key={key} label={label}>
            {key === "Reviewer Profile(method expertise)" ? (
              <Textarea value={valueOf(key)} onChange={(e) => setDraft((d) => ({ ...d, [key]: e.target.value }))} />
            ) : (
              <Input value={valueOf(key)} onChange={(e) => setDraft((d) => ({ ...d, [key]: e.target.value }))} />
            )}
          </Field>
        ) : (
          <div key={key}>
            <span className="text-xs font-medium text-slate-600">{label}</span>
            <RichText>{String(pitch[key] ?? "")}</RichText>
          </div>
        ),
      )}

      {/* Angel (Aug 2026): AE-suggested phrasing word bank under the three profile boxes —
          students pick one when evaluating a reviewer, or write their own. */}
      {canEdit ? (
        <Field label="AE-suggested expertise phrases (invite email)">
          <Textarea
            value={valueOf(AE_SUBDISCIPLINES_KEY)}
            onChange={(e) => setDraft((d) => ({ ...d, [AE_SUBDISCIPLINES_KEY]: e.target.value }))}
            placeholder={"vaccine epidemiology\ninfectious disease modeling\n…"}
          />
          <p className="mt-1 text-xs text-slate-500">
            One phrase per line (~3). Students can pick from these or write their own when
            completing a reviewer profile.
          </p>
        </Field>
      ) : aeSubs.length > 0 ? (
        <div className="rounded-md border border-blue-100 bg-blue-50/60 px-2 py-1.5">
          <p className="text-xs font-medium text-slate-600">
            AE-suggested expertise phrases — pick one when completing a reviewer profile, or write
            your own:
          </p>
          <div className="mt-1.5 flex flex-wrap gap-1.5">
            {aeSubs.map((sub) => (
              <Badge key={sub} tone="blue">
                {sub}
              </Badge>
            ))}
          </div>
        </div>
      ) : null}

      {(keyQuestion || canEdit) &&
        (canEdit ? (
          <Field label={KEY_Q_LABEL}>
            <Textarea
              value={valueOf(KEY_REVIEWER_QUESTION_KEY)}
              onChange={(e) => setDraft((d) => ({ ...d, [KEY_REVIEWER_QUESTION_KEY]: e.target.value }))}
            />
          </Field>
        ) : (
          <div>
            <span className="text-xs font-medium text-slate-600">{KEY_Q_LABEL}</span>
            <RichText>{keyQuestion}</RichText>
          </div>
        ))}

      {(similarArticles || canEdit) &&
        (canEdit ? (
          <Field label="Similar Articles">
            <Textarea
              value={valueOf("Nearest Neighbor Papers")}
              onChange={(e) => setDraft((d) => ({ ...d, ["Nearest Neighbor Papers"]: e.target.value }))}
            />
          </Field>
        ) : (
          <div>
            <span className="text-xs font-medium text-slate-600">Similar Articles</span>
            <RichText>{similarArticles}</RichText>
          </div>
        ))}

      {s.modelReviewers.length > 0 && (
        <div>
          <span className="text-xs font-medium text-slate-600">Model Peer Reviewers</span>
          <ul className="mt-1 space-y-1">
            {s.modelReviewers.map((r) => (
              <li key={r.slot} className="flex flex-wrap items-center gap-2 text-sm">
                <Badge tone="slate">#{r.slot}</Badge>
                <span className="font-medium">{r.name || "—"}</span>
                {r.profileUrl && (
                  <a className="text-xs text-brand hover:underline" href={r.profileUrl} target="_blank" rel="noreferrer">Profile ↗</a>
                )}
                {r.pubmedUrl && (
                  <a className="text-xs text-brand hover:underline" href={r.pubmedUrl} target="_blank" rel="noreferrer">PubMed ↗</a>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}

      {canEdit && (
        <Button onClick={() => save.mutate()} disabled={!dirty || save.isPending}>
          {save.isPending ? "Saving…" : dirty ? "Save profile" : "Saved"}
        </Button>
      )}
    </div>
  );
}
